import { Button } from "@shared/ui";
import { Field, InlineError, inputClassName } from "../components/Field";
import StepBody from "../components/StepBody";
import StepFooter from "../components/StepFooter";
import StepHeading from "../components/StepHeading";
import { createIgnition, getSession, updateIgnition } from "../lib/axle";
import {
  getFieldHint,
  getFieldLabel,
  getMissingFieldError,
} from "../lib/fieldCopy";
import { identifyPosthogUser } from "../lib/posthog";

// External dependencies
import * as Sentry from "@sentry/browser";
import _, { get } from "lodash";
import { useEffect, useState } from "react";
import Phone, { isPossiblePhoneNumber } from "react-phone-number-input/input";
import { trackPromise } from "react-promise-tracker";
import Loader from "../components/Loader";
import { SENTRY_TAG_IGNITION_TOKEN } from "../constants";
import { checkIgnitionToken } from "../lib/utility";

// The nav is hidden on the interstitial, so its body carries the card's top padding.
const NO_NAV_TOP = "pt-12";

const Interstitial = ({
  step,
  nextStep,
  session,
  setSession,
  client,
  posthog,
}) => {
  // Initialize state
  const [hasIgnition, setHasIgnition] = useState(false);
  const [interstitialStep, setInterstitialStep] = useState();
  const [hasRequired, setHasRequired] = useState(true);
  const [userFields, setUserFields] = useState({
    ...session.user,
  });
  const [metadataFields, setMetadataFields] = useState({ ...session.metadata });
  const [error, setError] = useState(null);
  // The field id an error belongs to; null for errors about the whole form.
  const [errorField, setErrorField] = useState(null);
  const [identitySubmitButtonType, setIdentitySubmitButtonType] = useState("");
  const [loading, setLoading] = useState(true);

  // Reassign state variables into friendly names
  const ignitionConfig =
    _(session).get("config") ?? _(client).get("config.ignitionConfig");
  const logoUrl = _(ignitionConfig).get("interstitial.styles.logo");
  const primaryActionStyle = {
    backgroundColor: _(ignitionConfig).get(
      "interstitial.styles.colors.primary-action"
    ),
  };

  const meetsRequirements = (config, user, metadata) => {
    // Flatten user and metadata together
    const userMetadataMerged = _.merge(user, metadata);
    const userMetdataFieldsConfigFlattened = _([
      _(config).get("interstitial.user-fields"),
      _(config).get("interstitial.metadata-fields"),
    ])
      .chain()
      .flatten()
      .filter((i) => i)
      .value();

    // Initialize meetsRequirements as true and overrideRequirements as false
    let meetsRequirements = true;
    let overrideRequirements = false;

    // Check each required or override item to see if it has value in user or metadata objects
    _(userMetdataFieldsConfigFlattened)
      .chain()
      .filter((item) => item.required || item.override)
      .forEach((item) => {
        if (item.override && userMetadataMerged[item.value]) {
          overrideRequirements = true;
        }
        if (!userMetadataMerged[item.value]) {
          meetsRequirements = false;
        }
      })
      .value();

    // Return meetsRequirement flag
    return meetsRequirements || overrideRequirements ? true : false;
  };

  const createIgnitionSession = async (user, metadata, clientId) => {
    // Build user object from params, have to check each field as empty query params return as null vs undefined
    const buildUser = {
      ...(user.id && {
        id: user.id,
      }),
      ...(user.firstName && {
        firstName: user.firstName,
      }),
      ...(user.lastName && {
        lastName: user.lastName,
      }),
      ...(user.email && {
        email: user.email,
      }),
      ...(user.phone && {
        phone: user.phone,
      }),
    };

    // Build ignition params from client, user, and metadata
    const ignitionParams = {
      clientId: clientId,
      user: _(user).size() ? buildUser : undefined,
      metadata: _(metadata).size() ? metadata : undefined,
      ...(session.templateId && { template: session.templateId }),
    };

    // Create ignition session in differential, returns ignition session id
    const [ignitionResponse, ignitionError] = await createIgnition(
      ignitionParams
    );

    if (ignitionError) {
      switch (get(ignitionError, "code")) {
        case 500:
          console.log(JSON.stringify(ignitionError));
          return "axle-error";
        default:
          return "connection-error";
      }
    }

    Sentry.setTag(SENTRY_TAG_IGNITION_TOKEN, ignitionResponse.id);

    // Get the ignition session created above, and other ignition session details
    const [ignitionSession, ignitionSessionError] = await getSession(
      ignitionResponse.id
    );

    if (ignitionSessionError) {
      switch (get(ignitionSessionError, "code")) {
        case 500:
          console.log(JSON.stringify(ignitionSessionError));
          return "axle-error";
        case 400:
          console.log(get(ignitionSessionError, "message", ""));
          if (
            get(ignitionSessionError, "message", "")?.includes(
              "Session already completed."
            )
          ) {
            setError(`Sorry, you cannot reuse a completed session.`);
          } else {
            //message from ign-backend: "Invalid ignition token provided."
            setError("Invalid or expired link.");
          }
          return;
        default:
          return "connection-error";
      }
    }

    // Ensure origin is set in session if provided
    setSession({
      ...ignitionSession,
      origin: session.origin,
    });

    console.log(
      `Updating ignition session ${ignitionSession.id} in createIgnitionSession...`
    );

    // Update the ignition session we created earlier, with status "opened" and lastEvent
    const [updateIgnitionResponse, updateIgnitionError] = await updateIgnition(
      ignitionSession.id,
      {
        status: "opened",
        lastEvent: {
          type: `ignition.opened`,
          data: {
            token: ignitionSession.id,
            user: ignitionSession.user,
            client: client.id,
          },
          createdAt: new Date().toISOString(),
        },
      }
    );

    if (updateIgnitionError) {
      switch (get(updateIgnitionError, "code")) {
        case 500:
          console.log(JSON.stringify(updateIgnitionError));
          return "axle-error";
        default:
          return "connection-error";
      }
    }

    // Identify user in Posthog
    identifyPosthogUser(posthog, ignitionSession, client);
    return null; // if there were no errors, we return null from createIgnitionSession
  };

  useEffect(() => {
    posthog.capture("$pageview", { step });
    const setup = async () => {
      try {
        // Check if required fields for user and metadata is available
        const checkHasIgnition = _(session).get("id") ? true : false;

        setHasIgnition(checkHasIgnition);

        const checkRequired = meetsRequirements(
          ignitionConfig,
          _(session).get("user"),
          _(session).get("metadata")
        );

        setHasRequired(checkRequired);

        // Check which interstitial steps are enabled
        const isWelcome = _(ignitionConfig).get("interstitial.welcome.enabled");
        const isIdentity = _(ignitionConfig).get(
          "interstitial.identity.enabled"
        );

        // If welcome is enabled, render welcome
        if (isWelcome) {
          setInterstitialStep("welcome");
          return;
        }

        // If welcome is not enabled and requirements are not met, go to identity step
        if (!checkRequired) {
          if (isIdentity) {
            setInterstitialStep("identity");
            return;
          }
          // If identity is disabled, setError
          setError("Requirements not met. Please update Ignition config.");
          return;
        }

        let createdIgnitionSessionStatus = null;
        // Otherwise, create Ignition resource and skip to consent
        if (!checkHasIgnition) {
          createdIgnitionSessionStatus = await createIgnitionSession(
            _(session).get("user"),
            _(session).get("metadata"),
            _(client).get("id")
          );
        }

        nextStep(createdIgnitionSessionStatus ?? "consent");
        return;
      } finally {
        setLoading(false);
      }
    };
    trackPromise(setup());
  }, []);

  const welcomeAction = async (e) => {
    // Get action step for button triggering welcomeAction
    const buttonName = e.target.name;

    const actionStep = _(ignitionConfig).get(
      `interstitial.welcome.${buttonName}-action.step`
    );
    const isIdentity = _(ignitionConfig).get("interstitial.identity.enabled");

    // If requirements are not met, go to identity step
    if (!hasRequired) {
      if (isIdentity) {
        setInterstitialStep("identity");
        return;
      }
      // If identity is disabled, setError
      setError("Requirements not met. Please update Ignition config.");
      return;
    }

    // Don't proceed to next step if it is interstitial-identity, instead just rerender component
    if (actionStep === "interstitial-identity") {
      setInterstitialStep("identity");
      return;
    }

    let createdIgnitionSessionStatus = null;
    // Create Ignition resource and go to next step specified in action
    if (!hasIgnition) {
      createdIgnitionSessionStatus = await trackPromise(
        createIgnitionSession(
          _(session).get("user"),
          _(session).get("metadata"),
          _(client).get("id")
        )
      );
    }
    nextStep(createdIgnitionSessionStatus ?? actionStep);
  };

  const identityAction = async (e) => {
    e.preventDefault();

    // Report the first required field left empty, next to that field.
    const missingUserField = (
      _(ignitionConfig).get("interstitial.user-fields") ?? []
    ).find((item) => item.required && !userFields[item.value]);
    const missingMetadataField = (
      _(ignitionConfig).get("interstitial.metadata-fields") ?? []
    ).find((item) => item.required && !metadataFields[item.value]);

    if (missingUserField) {
      setErrorField(`interstitial-${missingUserField.value}`);
      setError(getMissingFieldError(missingUserField));
      return;
    }
    if (missingMetadataField) {
      setErrorField(`interstitial-${missingMetadataField.value}`);
      setError(
        getMissingFieldError(missingMetadataField, {
          [missingMetadataField.value]: missingMetadataField.placeholder,
        })
      );
      return;
    }
    setErrorField(null);

    // Check if primary or secondary button was submitted
    const buttonName = get(
      e,
      "nativeEvent.submitter.name",
      identitySubmitButtonType
    ); // Note: sometimes e.nativeEvent.submitter is undefined or null. In that case we will use identitySubmitButtonType as backup
    const actionStep = _(ignitionConfig).get(
      `interstitial.identity.${buttonName}-action.step`
    );

    try {
      // Don't proceed to next step if it is interstitial-welcome, instead just rerender component
      if (actionStep === "interstitial-welcome") {
        setInterstitialStep("welcome");
        return;
      }

      let createdIgnitionSessionStatus = null;
      // Update or create Ignition with data entered into user and metadata fields
      if (hasIgnition) {
        const [updateIgnitionResponse, updateIgnitionError] =
          await updateIgnition(_(session).get("id"), {
            ...session,
            ...{ user: userFields, metadata: metadataFields },
          });

        if (updateIgnitionError) {
          switch (get(updateIgnitionError, "code")) {
            case 500:
              console.log(JSON.stringify(updateIgnitionError));
              nextStep("axle-error");
              return;
            default:
              nextStep("connection-error");
              return;
          }
        }

        setSession({
          ...session,
          ...{ user: userFields, metadata: metadataFields },
        });
      } else {
        createdIgnitionSessionStatus = await trackPromise(
          createIgnitionSession(userFields, metadataFields, _(client).get("id"))
        );
      }

      // Go to next step specified in actionStep or if there was a failure in createIgnitionSession, overwrite actionStep
      nextStep(createdIgnitionSessionStatus ?? actionStep);
    } catch (error) {
      // If 500 send customer to "failed"
      if (
        error.message === "Oops! Something went wrong." ||
        error.message === "Oops something went wrong. Please try again later."
      ) {
        nextStep("failed");
      }
      // Else display error
      setError(error.message);
    }
  };

  const fieldError = (id) => (errorField === id ? error : undefined);
  const fieldErrorProps = (id) =>
    errorField === id
      ? { "aria-invalid": true, "aria-describedby": `${id}-error` }
      : {};

  const renderSwitch = (step) => {
    switch (step) {
      case "welcome":
        return (
          <>
            <StepBody className={NO_NAV_TOP} scrollDivider={false}>
              <div className="mb-12 flex">
                <img
                  src={logoUrl}
                  className="max-h-12 max-w-full h-full w-auto"
                ></img>
              </div>
              <StepHeading
                size="hero"
                title={_(ignitionConfig).get("interstitial.welcome.header")}
                description={_(ignitionConfig).get(
                  "interstitial.welcome.subheader"
                )}
              />
            </StepBody>
            <StepFooter>
              {_(ignitionConfig).get("interstitial.welcome.primary-action") && (
                <Button
                  onClick={welcomeAction}
                  name={"primary"}
                  style={primaryActionStyle}
                >
                  {_(ignitionConfig).get(
                    "interstitial.welcome.primary-action.text"
                  )}
                </Button>
              )}
              {_(ignitionConfig).get(
                "interstitial.welcome.secondary-action"
              ) && (
                <Button
                  variant="secondary"
                  type="button"
                  onClick={welcomeAction}
                  name={"secondary"}
                >
                  {_(ignitionConfig).get(
                    "interstitial.welcome.secondary-action.text"
                  )}
                </Button>
              )}
            </StepFooter>
          </>
        );
      case "identity":
        return (
          <>
            <form
              className="flex min-h-0 flex-1 flex-col"
              onSubmit={identityAction}
            >
              <StepBody className={NO_NAV_TOP} scrollDivider={false}>
                <div className="flex">
                  <img
                    src={logoUrl}
                    className="max-h-12 max-w-full h-full w-auto"
                  ></img>
                </div>
                <StepHeading
                  title={_(ignitionConfig).get("interstitial.identity.header")}
                  description={_(ignitionConfig).get(
                    "interstitial.identity.subheader"
                  )}
                />
                <div className="flex flex-col gap-4">
                  {error && !errorField && (
                    <div
                      className="rounded border border-danger-border p-3"
                      role="status"
                    >
                      <InlineError>{error}</InlineError>
                    </div>
                  )}
                  {_(ignitionConfig)
                    .chain()
                    .get("interstitial.user-fields")
                    .map((item) => {
                      return item.type === "phone" ? (
                        <Field
                          key={item.value}
                          id={`interstitial-${item.value}`}
                          label={getFieldLabel(item)}
                          optional={item.required === false}
                          error={
                            fieldError(`interstitial-${item.value}`) ??
                            (userFields[item.value] &&
                            !isPossiblePhoneNumber(userFields[item.value], "US")
                              ? "Invalid phone number"
                              : undefined)
                          }
                        >
                          <Phone
                            id={`interstitial-${item.value}`}
                            {...fieldErrorProps(`interstitial-${item.value}`)}
                            placeholder={getFieldHint(item)}
                            defaultCountry="US"
                            value={userFields[item.value]}
                            className={inputClassName}
                            onChange={(e) =>
                              setUserFields({
                                ...userFields,
                                [item.value]: e,
                              })
                            }
                          />
                        </Field>
                      ) : (
                        <Field
                          key={item.value}
                          id={`interstitial-${item.value}`}
                          label={getFieldLabel(item)}
                          optional={item.required === false}
                          error={fieldError(`interstitial-${item.value}`)}
                        >
                          <input
                            id={`interstitial-${item.value}`}
                            {...fieldErrorProps(`interstitial-${item.value}`)}
                            placeholder={getFieldHint(item)}
                            value={userFields[item.value]}
                            className={inputClassName}
                            type={item.type}
                            onChange={(e) =>
                              setUserFields({
                                ...userFields,
                                [item.value]: e.target.value,
                              })
                            }
                          />
                        </Field>
                      );
                    })
                    .value()}
                  {_(ignitionConfig)
                    .chain()
                    .get("interstitial.metadata-fields")
                    .map((item) => {
                      // Metadata keys are the client's own names (e.g. RA), so the client's text is the
                      // label as written (it carries its own "(optional)") and the box stays empty.
                      return (
                        <Field
                          key={item.value}
                          id={`interstitial-${item.value}`}
                          label={item.placeholder}
                          error={fieldError(`interstitial-${item.value}`)}
                        >
                          <input
                            id={`interstitial-${item.value}`}
                            {...fieldErrorProps(`interstitial-${item.value}`)}
                            value={metadataFields[item.value]}
                            className={inputClassName}
                            type={item.type}
                            onChange={(e) =>
                              setMetadataFields({
                                ...metadataFields,
                                [item.value]: e.target.value,
                              })
                            }
                          />
                        </Field>
                      );
                    })
                    .value()}
                </div>
              </StepBody>
              <StepFooter>
                {_(ignitionConfig).get(
                  "interstitial.identity.primary-action"
                ) && (
                  <Button
                    type={"submit"}
                    name={"primary"}
                    style={primaryActionStyle}
                    onClick={() => setIdentitySubmitButtonType("primary")}
                  >
                    {_(ignitionConfig).get(
                      "interstitial.identity.primary-action.text"
                    )}
                  </Button>
                )}
                {_(ignitionConfig).get(
                  "interstitial.identity.secondary-action"
                ) && (
                  <Button
                    variant="secondary"
                    name={"secondary"}
                    type={"submit"}
                    onClick={() => setIdentitySubmitButtonType("secondary")}
                  >
                    {_(ignitionConfig).get(
                      "interstitial.identity.secondary-action.text"
                    )}
                  </Button>
                )}
              </StepFooter>
            </form>
          </>
        );
      default:
        return (
          <StepBody className={NO_NAV_TOP} scrollDivider={false}>
            <div
              className="rounded border border-danger-border p-3 text-body-lg text-danger-ink"
              role="status"
            >
              {error ? error : "Sorry, this page does not exist!"}
            </div>
          </StepBody>
        );
    }
  };

  return (
    <>
      {loading ? <Loader isOpen={loading} /> : renderSwitch(interstitialStep)}
    </>
  );
};

export default Interstitial;
